import { useState } from 'react';
import type { ResearchAgreementWithStudentName } from '../../Types/interfaces';
import { ResearchAgreementStatus } from '../../Types/interfaces';
import {
  GetResearchAgreementsOfCoordonator,
  GetResearchAgreementsOfTutor,
  RejectResearchAgreementByTutor,
  SignResearchAgreementByTutor,
  SignResearchAgreementByUniversityCoordinator,
} from '../../helper/apiHelper';
import { Grid, Input } from 'antd';
import {
  Button,
  Card,
  Divider,
  message,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { PiStudentFill } from 'react-icons/pi';
import {
  EMPTY_ARRAY,
  RESEARCH_AGREEMENT_STATUS_COLORS,
  RESEARCH_AGREEMENT_STATUS_LOCALE_KEYS,
} from '../../Constants/constants';
import { useTranslation } from 'react-i18next';
import FileUploader from '../FileUploader';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const { Text, Paragraph } = Typography;
const { Search } = Input;
const { useBreakpoint } = Grid;

const getIsSignatureDisabled = (
  record: ResearchAgreementWithStudentName,
  isCoordinator: boolean
): boolean => {
  if (isCoordinator) {
    return !record.tutorSigned || record.universityCoordinatorSigned;
  }

  // Tutor
  return (
    record.status === ResearchAgreementStatus.Accepted ||
    record.status === ResearchAgreementStatus.Active ||
    record.status === ResearchAgreementStatus.Rejected ||
    record.status === ResearchAgreementStatus.Generated
  );
};

const getIsRejectButtonDisabled = (
  record: ResearchAgreementWithStudentName,
  isCoordinator: boolean
): boolean => {
  if (isCoordinator) {
    return true;
  }

  return record.status === ResearchAgreementStatus.Accepted;
};

interface ListResearchAgreementsComponentProps {
  isCoordinator?: boolean;
}

const ListResearchAgreementsComponent = ({
  isCoordinator = false,
}: ListResearchAgreementsComponentProps): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const screens = useBreakpoint();

  const {
    data: researchAgreements = EMPTY_ARRAY,
    isLoading,
    refetch: reFetchData,
  } = useQuery({
    queryKey: [
      ...QUERY_KEYS.RESEARCH_AGREEMENTS,
      loggedInUser.id,
      isCoordinator,
    ],
    queryFn: async () => {
      let agreements: ResearchAgreementWithStudentName[];
      if (isCoordinator) {
        agreements = await GetResearchAgreementsOfCoordonator(loggedInUser.id);
      } else {
        agreements = await GetResearchAgreementsOfTutor(loggedInUser.id);
      }
      return agreements;
    },
    enabled: !!loggedInUser.id,
  });

  const filteredResearchAgreements = researchAgreements.filter((agreement) => {
    // if the search term is empty, return all elements
    if (!searchTerm) return true;

    return agreement.studentName.toLowerCase().includes(searchTerm);
  });

  if (isLoading) {
    return (
      <div style={{ marginTop: '40px', paddingLeft: '30px' }}>
        {Array(6)
          .fill(null)
          .map((_, i) => {
            return (
              <div key={i} style={{ paddingBottom: 10 }}>
                <Skeleton active title={false} paragraph={{ rows: 2 }} />
              </div>
            );
          })}
      </div>
    );
  }

  const signatureStatus = (
    agreement: ResearchAgreementWithStudentName
  ): React.ReactNode => {
    const { tutorSigned, universityCoordinatorSigned, status } = agreement;

    const renderStatus = (
      color: string,
      message: string
    ): React.ReactElement => {
      if (color === 'orange') {
        return (
          <Space>
            <ExclamationCircleFilled style={{ color: color }} />
            <span>{message}</span>
          </Space>
        );
      }
      return (
        <Space>
          <CheckCircleFilled style={{ color: color }} />
          <span>{message}</span>
        </Space>
      );
    };

    // Coordinator
    if (isCoordinator) {
      if (tutorSigned && !universityCoordinatorSigned) {
        return renderStatus(
          'blue',
          t('researchAgreementComp.readyToSignByYou')
        );
      }
      if (!tutorSigned) {
        return renderStatus(
          'orange',
          t('researchAgreementComp.waitingToSignByTutor')
        );
      }
      return renderStatus('green', t('researchAgreementComp.alreadySigned'));
    }

    // Tutor
    if (
      (!tutorSigned && status === ResearchAgreementStatus.Active) ||
      status === ResearchAgreementStatus.Rejected
    ) {
      return renderStatus(
        'orange',
        t('researchAgreementComp.waitWhileStudentFill')
      );
    }
    if (!tutorSigned && status === ResearchAgreementStatus.Generated) {
      return renderStatus(
        'orange',
        t('researchAgreementComp.waitingToBeSignedByStudent')
      );
    }
    if (!tutorSigned && status === ResearchAgreementStatus.InReview) {
      return renderStatus('blue', t('researchAgreementComp.readyToSignByYou'));
    }
    return renderStatus('green', t('researchAgreementComp.alreadySigned'));
  };

  const handleSearch = (value: string): void => {
    setSearchTerm(value.toLowerCase().trim());
  };

  const viewAndRejectButton = (agreement: ResearchAgreementWithStudentName) => {
    return (
      <div style={{ display: 'flex', gap: '8px' }}>
        {/* View ResearchAgreement document */}
        <Button
          type="primary"
          href={`${agreement.researchAgreementDocumentUrl}?status=${ResearchAgreementStatus[agreement.status]}`}
          target="_blank"
        >
          {t('researchAgreementComp.view')}
        </Button>

        {/* Reject button */}
        {!isCoordinator &&
          agreement.status === ResearchAgreementStatus.InReview && (
            <Button
              danger
              disabled={getIsRejectButtonDisabled(agreement, isCoordinator)}
              onClick={async () => {
                try {
                  await RejectResearchAgreementByTutor(
                    loggedInUser.id,
                    agreement.internshipProcessId
                  );
                  await reFetchData();
                  messageApi.success(t('researchAgreementComp.rejected'));
                } catch {
                  messageApi.error(t('researchAgreementComp.errorWhenReject'));
                }
              }}
            >
              {t('researchAgreementComp.reject')}
            </Button>
          )}
      </div>
    );
  };

  const tabItems = filteredResearchAgreements.map((agreement) => {
    return {
      key: agreement.researchAgreementId.toString(),
      label: agreement.studentName,
      children: (
        <div style={{ padding: '0 16px' }}>
          <Card
            title={t('researchAgreementComp.researchTopicAgreement')}
            extra={screens.sm ? viewAndRejectButton(agreement) : null} // View agreement and Reject button
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            styles={{
              body: {
                paddingTop: 10,
              },
            }}
          >
            {/* View agreement and Reject button */}
            {!screens.sm && (
              <div style={{ paddingBottom: 25 }}>
                {viewAndRejectButton(agreement)}
              </div>
            )}

            {/* Data Grid Layout */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '20px',
              }}
            >
              <div>
                <Text type="secondary">{t('researchAgreementComp.field')}</Text>
                <div style={{ fontWeight: 500 }}>
                  {agreement.fieldOfResearch}
                </div>
              </div>
              <div>
                <Text type="secondary">
                  {t('researchAgreementComp.researchTopic')}
                </Text>
                <div style={{ fontWeight: 500 }}>{agreement.researchTopic}</div>
              </div>
              <div>
                <Text type="secondary">
                  {t('researchAgreementComp.status')}
                </Text>
                <div>
                  <Tag
                    color={RESEARCH_AGREEMENT_STATUS_COLORS[agreement.status]}
                  >
                    {t(RESEARCH_AGREEMENT_STATUS_LOCALE_KEYS[agreement.status])}
                  </Tag>
                </div>
              </div>
              <div>
                <Text type="secondary">{t('researchAgreementComp.sign')}</Text>
                <div>{signatureStatus(agreement)}</div>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <Text type="secondary">
                {t('researchAgreementComp.researchDesc')}
              </Text>
              <div
                style={{
                  backgroundColor: '#fafafa',
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '4px',
                  border: '1px solid #f0f0f0',
                }}
              >
                <Paragraph style={{ margin: 0 }}>
                  {agreement.descriptionOfResearch || 'Nincs megadva leírás.'}
                </Paragraph>
              </div>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            <div>
              <FileUploader
                alreadySigned={
                  isCoordinator
                    ? agreement.universityCoordinatorSigned
                    : agreement.tutorSigned
                }
                apiUrl={'research-agreement/upload'}
                documentUrl={agreement.researchAgreementDocumentUrl}
                documentSigningStatus={
                  ResearchAgreementStatus[agreement.status]
                }
                isSignatureDisabled={getIsSignatureDisabled(
                  agreement,
                  isCoordinator
                )}
                isVisible={!getIsSignatureDisabled(agreement, isCoordinator)}
                onFileUpload={async () => {
                  if (isCoordinator) {
                    try {
                      await SignResearchAgreementByUniversityCoordinator(
                        loggedInUser.id,
                        agreement.internshipProcessId
                      );
                      messageApi.success('Sikeres aláírás!');
                      await reFetchData();
                    } catch {
                      messageApi.error(t('researchAgreementComp.signError'));
                    }
                  } else {
                    try {
                      await SignResearchAgreementByTutor(
                        loggedInUser.id,
                        agreement.internshipProcessId
                      );
                      messageApi.success('Sikeres aláírás!');
                      await reFetchData();
                    } catch {
                      messageApi.error('Hiba történt az aláírás során.');
                    }
                  }
                }}
              />
            </div>
          </Card>
        </div>
      ),
    };
  });

  return (
    <>
      {contextHolder}
      <Search
        prefix={<PiStudentFill fontSize={22} />}
        placeholder={t('researchAgreementComp.searchPlaceholder')}
        allowClear
        onChange={(e) => handleSearch(e.target.value)}
      />
      <Tabs
        tabPosition={screens.lg ? 'left' : 'top'}
        items={tabItems}
        style={{ marginTop: '20px' }}
      />
    </>
  );
};
export default ListResearchAgreementsComponent;
